"use client";

import { useEffect, useRef, useState } from "react";

export type ParsedAddress = {
  addressLine: string;
  city: string;
  state: string;
  pincode: string;
};

type AddressSuggestion = ParsedAddress & {
  id: string;
  label: string;
};

type AddressAutocompleteProps = {
  value: ParsedAddress;
  onChange: (next: ParsedAddress) => void;
};

function pickCity(address: Record<string, string | undefined>) {
  return (
    address.city
    || address.town
    || address.village
    || address.suburb
    || address.county
    || address.municipality
    || address.state_district
    || ""
  );
}

function mapNominatim(item: {
  place_id: number;
  display_name: string;
  address?: Record<string, string>;
}): AddressSuggestion {
  const address = item.address || {};
  const lineParts = [
    address.house_number,
    address.road || address.pedestrian || address.neighbourhood,
    address.suburb || address.locality,
  ].filter(Boolean);
  return {
    id: String(item.place_id),
    label: item.display_name,
    addressLine: lineParts.join(", ") || item.display_name.split(",").slice(0, 3).join(",").trim(),
    city: pickCity(address),
    state: address.state || "",
    pincode: address.postcode || "",
  };
}

async function searchIndiaAddresses(query: string): Promise<AddressSuggestion[]> {
  const url = new URL("https://nominatim.openstreetmap.org/search");
  url.searchParams.set("format", "jsonv2");
  url.searchParams.set("addressdetails", "1");
  url.searchParams.set("countrycodes", "in");
  url.searchParams.set("limit", "6");
  url.searchParams.set("q", query);
  const response = await fetch(url.toString(), {
    headers: {
      Accept: "application/json",
      // Nominatim requires an identifying app string.
      "Accept-Language": "en-IN,en;q=0.9",
    },
  });
  if (!response.ok) throw new Error("Address lookup failed");
  const rows = await response.json() as Array<{
    place_id: number;
    display_name: string;
    address?: Record<string, string>;
  }>;
  return rows.map(mapNominatim);
}

async function lookupIndianPincode(pincode: string): Promise<Partial<ParsedAddress> | null> {
  const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
  if (!response.ok) return null;
  const payload = await response.json() as Array<{
    Status?: string;
    PostOffice?: Array<{ Name?: string; District?: string; State?: string; Block?: string }>;
  }>;
  const office = payload[0]?.PostOffice?.[0];
  if (!office || payload[0]?.Status !== "Success") return null;
  return {
    city: office.District || office.Block || office.Name || "",
    state: office.State || "",
    pincode,
  };
}

export default function AddressAutocomplete({ value, onChange }: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState("Start typing — suggestions for India addresses appear below");
  const boxRef = useRef<HTMLDivElement>(null);
  const skipSearchRef = useRef(false);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!boxRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (skipSearchRef.current) {
      skipSearchRef.current = false;
      return;
    }
    const query = value.addressLine.trim();
    if (query.length < 3) {
      setSuggestions([]);
      setOpen(false);
      setHint("Type at least 3 characters for address suggestions");
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      void searchIndiaAddresses(query)
        .then((rows) => {
          if (cancelled) return;
          setSuggestions(rows);
          setOpen(rows.length > 0);
          setHint(rows.length ? "Pick a suggestion to auto-fill city, state & pincode" : "No matches — keep typing or fill city/state manually");
        })
        .catch(() => {
          if (cancelled) return;
          setSuggestions([]);
          setHint("Address search unavailable — fill city, state and pincode manually");
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [value.addressLine]);

  useEffect(() => {
    const pin = value.pincode.trim();
    if (!/^[1-9][0-9]{5}$/.test(pin)) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void lookupIndianPincode(pin).then((parsed) => {
        if (cancelled || !parsed) return;
        onChange({
          ...value,
          city: value.city.trim() || parsed.city || "",
          state: value.state.trim() || parsed.state || "",
          pincode: pin,
        });
        setHint("Pincode matched — city/state filled from India Post");
      });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [value.pincode]);

  const choose = (item: AddressSuggestion) => {
    skipSearchRef.current = true;
    onChange({
      addressLine: item.addressLine || item.label,
      city: item.city,
      state: item.state,
      pincode: item.pincode,
    });
    setSuggestions([]);
    setOpen(false);
    setHint("Address applied — city, state and pincode filled automatically");
  };

  return (
    <div className="address-auto" ref={boxRef}>
      <label className="address-auto-label">
        Full address
        <input
          value={value.addressLine}
          onChange={(e) => onChange({ ...value, addressLine: e.target.value })}
          onFocus={() => suggestions.length && setOpen(true)}
          placeholder="House / street / landmark / area"
          autoComplete="street-address"
        />
      </label>
      {open && suggestions.length > 0 && (
        <ul className="address-suggestions" role="listbox">
          {suggestions.map((item) => (
            <li key={item.id}>
              <button type="button" onClick={() => choose(item)}>
                <b>{item.addressLine}</b>
                <small>{[item.city, item.state, item.pincode].filter(Boolean).join(" · ") || item.label}</small>
              </button>
            </li>
          ))}
        </ul>
      )}
      <small className="address-hint">{loading ? "Searching addresses…" : hint}</small>

      <label>City
        <input
          value={value.city}
          onChange={(e) => onChange({ ...value, city: e.target.value })}
          placeholder="Auto-filled from address"
          autoComplete="address-level2"
        />
      </label>
      <label>State
        <input
          value={value.state}
          onChange={(e) => onChange({ ...value, state: e.target.value })}
          placeholder="Auto-filled from address"
          autoComplete="address-level1"
        />
      </label>
      <label>Pincode
        <input
          inputMode="numeric"
          maxLength={6}
          value={value.pincode}
          onChange={(e) => onChange({ ...value, pincode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
          placeholder="302017"
          autoComplete="postal-code"
        />
      </label>
    </div>
  );
}
